import { TASK_STATUS, TEMPLATE_TYPES } from "config/constants/constants";
import i18n from "multilingual";


export const normalizeTaskStatus = (status = "") => {
  const s = String(status).toUpperCase();
  switch (s) {
    case "READY":
      return TASK_STATUS.QUEUED;
    case "RUNNING":
      return TASK_STATUS.IN_PROGRESS;
    case "IN-PROGRESS":
      return TASK_STATUS.IN_PROGRESS;
    case "IN_PROGRESS":
      return TASK_STATUS.IN_PROGRESS;
    case "COMPLETED":
      return TASK_STATUS.COMPLETED;
    case "SUCCESS":
      return TASK_STATUS.COMPLETED;
    case "FAILED":
      return TASK_STATUS.FAILED;
    case "ERROR":
      return TASK_STATUS.FAILED;
    default:
      return TASK_STATUS.PENDING;
  }
};

const getTaskMessages = () => ({
  template: {
    TITLE: i18n.t("common.CREATING_CUSTOM_TEMPLATE"),
    INITIAL: i18n.t("template.initial"),
    GROUPS: {
      "task_group@dynamic-template.template-classifier.execute":
        i18n.t("template.groups.task_group_dynamic_template_template_classifier_execute"),
      "task_group@dynamic-template.create.execute":
        i18n.t("template.groups.task_group_dynamic_template_create_execute"),
      "task_group@dynamic-template.generate-preview.execute":
        i18n.t("template.groups.task_group_dynamic_template_generate_preview_execute"),
    },
    COMPLETED: i18n.t("template.completed"),
    FAILED: i18n.t("template.failed"),
  },

  soap: {
    INITIAL: i18n.t("soap.initial"),
    GROUPS: {
      "task_group@notes.soapnote.generate":
        i18n.t("soap.groups.task_group_notes_soapnote_generate"),
      "task_group@conversation.title.generate":
        i18n.t("soap.groups.task_group_conversation_title_generate"),
      "notes.soapnote.generate":
        i18n.t("soap.groups.notes_soapnote_generate"),
      "task_group@end-conversation-notes.generate":
        i18n.t("soap.groups.task_group_end_conversation_notes_generate"),
    },
    COMPLETED: i18n.t("soap.completed"),
    FAILED: i18n.t("soap.failed"),
  },
});


export const TASK_MESSAGES = getTaskMessages();

export const calculateProgress = (task, prevProgress = 0) => {
  if (!task) {
    return Math.max(prevProgress, 5);
  }

  const {
    progress,
    status,
    totalTaskGroups,
    processedTaskGroups
  } = task;

  let newProgress = prevProgress;
  if (typeof progress === "number") {
    newProgress = progress;
  } else {
    switch (status) {
      case TASK_STATUS.COMPLETED:
        newProgress = 100;
        break;
      case TASK_STATUS.IN_PROGRESS:
        const groupProgress =
          totalTaskGroups > 0
            ? (processedTaskGroups / totalTaskGroups) * 95
            : 50;
        newProgress = groupProgress + 5;
        break;
      case TASK_STATUS.PENDING:
        newProgress = 10;
        break;
      case TASK_STATUS.FAILED:
        newProgress = 0;
        break;
      default:
        newProgress = prevProgress;
    }
  }

  const roundedProgress = Math.round(newProgress);

  return Math.min(Math.max(roundedProgress, prevProgress), 100);
};

export const extractAndParseJsonPayload = (fhirTask) => {
  try {
    const rawString = fhirTask?.output?.[0]?.valueString;
    return rawString ? JSON.parse(rawString) : null;
  } catch (err) {
    console.warn("Failed to parse task output JSON:", err);
    return null;
  }
};

export const extractTemplateName = (taskData) => {
  try {
    const templateName =
      taskData?.taskGroups?.[0]?.tasks?.[0]?.result?.templateName;
    return templateName || null;
  } catch {
    return null;
  }
};

export const extractSOAPNoteTitle = (taskData) => {
  try {
    const title = taskData?.taskGroups
      ?.find((g) => g.code === "task_group@notes.soapnote.generate")
      ?.tasks?.find((t) => t.code === "conversation.title.generate")
      ?.result?.title;
    return title || null;
  } catch {
    return null;
  }
};
export const extractSoapGenerationError = (parsedPayload) => {

  // Check root level error
  if (parsedPayload?.error?.message) {
    return parsedPayload.error.message;
  }
  if (typeof parsedPayload?.error === "string") {
    return parsedPayload.error;
  }

  if (Array.isArray(parsedPayload?.taskGroups)) {
    for (const group of parsedPayload.taskGroups) {
      // Check all tasks in this group for failures
      if (Array.isArray(group.tasks)) {
        const failedTaskNames = [];

        for (const task of group.tasks) {
          if (normalizeTaskStatus(task.status) === TASK_STATUS.FAILED) {
            if (task.metadata?.promptName) {
              failedTaskNames.push(task.metadata.promptName);
            } else if (task.name) {
              failedTaskNames.push(task.name);
            } else if (task.error?.message) {
              failedTaskNames.push(task.error.message);
            }
          }
        }

        // If we found failed tasks in this group, build error message
        if (failedTaskNames.length > 0) {
          let errorMessage = "";

          // Try to get group-level error message first
          if (group.error?.message) {
            errorMessage = group.error.message;
          } else if (typeof group.error === "string") {
            errorMessage = group.error;
          }

          // Append failed task names
          const tasksPart = failedTaskNames.length === 1
            ? `${failedTaskNames[0]}`
            : `${failedTaskNames.join(", ")}`;

          errorMessage = errorMessage
            ? `${errorMessage} ${tasksPart}`
            : tasksPart;

          // Add template message for specific group code
          if (group?.code === 'task_group@end-conversation-notes.generate') {
            errorMessage += `. ${i18n.t('common.PLEASE_TRY_GENERATING_IN_ENCOUNTER')}`;
          }

          return errorMessage;
        }
      }

      // Also check if the group itself failed (without failed tasks)
      if (normalizeTaskStatus(group.status) === TASK_STATUS.FAILED) {
        if (group.error?.message) {
          return group.error.message;
        } else if (typeof group.error === "string") {
          return group.error;
        }
      }
    }
  }

  return null;
};

const genericParse = (fhirTask) => {
  const rootStatus = normalizeTaskStatus(fhirTask.status);

  if (rootStatus === TASK_STATUS.QUEUED) {
    return {
      status: TASK_STATUS.QUEUED,
      progress: 10,
      message: i18n.t('common.PREPARING_YOUR_REQUEST'),
      taskGroups: [],
    };
  }

  const parsedPayload = extractAndParseJsonPayload(fhirTask);
  console.log("Parsing FHIR Task:", parsedPayload);

  if (parsedPayload) {
    // Check for any failed tasks first (regardless of group status)
    if (Array.isArray(parsedPayload.taskGroups)) {
      const hasFailedTask = parsedPayload.taskGroups.some(group =>
        Array.isArray(group.tasks) && group.tasks.some(task =>
          normalizeTaskStatus(task.status) === TASK_STATUS.FAILED
        )
      );

      if (hasFailedTask) {
        return {
          ...parsedPayload,
          status: TASK_STATUS.FAILED,
          error: extractSoapGenerationError(parsedPayload) || "A sub-task has failed.",
        };
      }

      // Then check for failed groups
      const hasFailedGroup = parsedPayload.taskGroups.some(group =>
        normalizeTaskStatus(group.status) === TASK_STATUS.FAILED
      );

      if (hasFailedGroup) {
        return {
          ...parsedPayload,
          status: TASK_STATUS.FAILED,
          error: extractSoapGenerationError(parsedPayload),
        };
      }
    }
    return {
      ...parsedPayload,
      status: normalizeTaskStatus(parsedPayload.status),
    };
  }
  return {
    status: rootStatus,
    progress: 10,
    message: i18n.t('common.INITIALIZING'),
    taskGroups: [],
  };
};

const findActiveTaskGroup = (taskData) => {
  if (!taskData || !Array.isArray(taskData.taskGroups)) return null;

  const { taskGroups, currentTaskGroupCode } = taskData;

  let activeGroup = taskGroups.find(
    (g) => normalizeTaskStatus(g.status) === TASK_STATUS.IN_PROGRESS
  );
  if (activeGroup) return activeGroup;

  const currentGroup = taskGroups.find((g) => g.code === currentTaskGroupCode);
  if (
    currentGroup &&
    normalizeTaskStatus(currentGroup.status) !== TASK_STATUS.COMPLETED
  ) {
    return currentGroup;
  }

  activeGroup = taskGroups.find(
    (g) => normalizeTaskStatus(g.status) === TASK_STATUS.PENDING
  );
  if (activeGroup) return activeGroup;

  return currentGroup;
};

export const getTaskMessage = (taskData, taskCategory = "template") => {
  if (!taskData) return i18n.t('common.INITIALIZING');

  const taskMessages = getTaskMessages();
  const categoryMessages = taskMessages[taskCategory] || {};
  const { status } = taskData;

  if (status === TASK_STATUS.QUEUED) {
    return taskData.message || i18n.t('common.PREPARING_YOUR_REQUEST');
  }
  if (status === TASK_STATUS.COMPLETED) {
    return (categoryMessages.COMPLETED) || i18n.t('common.TASK_COMPLETED_SUCCESSFULLY');
  }
  if (status === TASK_STATUS.FAILED) {
    return (categoryMessages.FAILED) || i18n.t('common.TASK_FAILED');
  }

  const activeGroup = findActiveTaskGroup(taskData);

  if (activeGroup?.code && categoryMessages.GROUPS) {
    const activeTask = activeGroup.tasks?.find(
      (t) => normalizeTaskStatus(t.status) === "RUNNING"
    );

    if (activeTask?.code && categoryMessages.GROUPS[activeTask.code]) {
      return (categoryMessages.GROUPS[activeTask.code]);
    }

    return (
      (categoryMessages.GROUPS[activeGroup.code]) ||
      `Processing: ${activeGroup.code}`
    );
  }

  return (categoryMessages.INITIAL) || "Processing your request...";
};

const TemplateProcessor = {
  getInitialState: (taskId) => ({
    id: taskId,
    title: getTaskMessages().template.TITLE,
    message: getTaskMessages().template.INITIAL,
    progress: 10,
    status: TASK_STATUS.IN_PROGRESS,
  }),

  parse: genericParse,

  extractFinalPayload: (fhirTask) => {
    const parsedOutput = extractAndParseJsonPayload(fhirTask);
    if (!parsedOutput?.taskGroups) {
      throw new Error("Invalid or missing task groups in final output");
    }

    const classifierTask = parsedOutput.taskGroups.find((t) =>
      t.code?.includes("template-classifier.execute")
    )?.tasks?.[0];
    if (!classifierTask?.result) {
      throw new Error("Classifier task result not found");
    }

    const { templateType, templateName } = classifierTask.result;
    const contentTask = TemplateProcessor.getContentTask(
      parsedOutput.taskGroups,
      templateType
    );
    if (!contentTask?.result) {
      throw new Error(`Content task result not found for type ${templateType}`);
    }

    return TemplateProcessor.buildPayload(
      contentTask,
      templateType,
      templateName
    );
  },

  getCompletionActionText: () => "View template",

  getContentTask: (taskGroups, templateType) => {
    const taskCode =
      templateType === TEMPLATE_TYPES.QUESTIONNAIRE
        ? "template.create.execute"
        : "template.generate-preview.execute";
    return taskGroups.find((t) => t.code.includes(taskCode))?.tasks?.[0];
  },

  buildPayload: (contentTask, templateType, templateName) => {
    const { llmPrompt: { promptCode, promptName } = {} } = contentTask.result;
    const payload = {
      templateCode: promptCode,
      templateName: promptName || templateName,
      templateType,
    };

    if (templateType === TEMPLATE_TYPES.QUESTIONNAIRE) {
      payload.fhirQuestionnaire =
        contentTask?.result?.llmPrompt?.fhirQuestionnaire;
    } else if (templateType === TEMPLATE_TYPES.NOTE) {
      payload.templatePreview =
        contentTask?.result?.llmPrompt?.promptOutputPreview;
    }
    return payload;
  },
};

const SOAPProcessor = {
  getInitialState: (taskId) => ({
    id: taskId,
    title: i18n.t('notes.soapnote.generate'),
    message: getTaskMessages().soap.INITIAL,
    progress: 10,
    status: TASK_STATUS.QUEUED,
  }),

  parse: genericParse,

  extractFinalPayload: (fhirTask) => {
    const parsedOutput = extractAndParseJsonPayload(fhirTask);
    if (!parsedOutput?.taskGroups) {
      const status = normalizeTaskStatus(fhirTask.status);
      if (status === TASK_STATUS.FAILED || status === TASK_STATUS.COMPLETED) {
        throw new Error("Invalid or missing task groups in SOAP note output");
      }
      return null;
    }

    const compositionResult = parsedOutput.taskGroups
      ?.find((f) => f?.code === "task_group@notes.soapnote.generate")
      ?.tasks?.find((f) => f?.code === "conversation.title.generate")?.result;

    if (!compositionResult) {
      console.warn(
        "Primary SOAP note result not found, checking for other notes."
      );
    }

    return compositionResult || parsedOutput;
  },

  getCompletionActionText: () => "View SOAP Note",
};

export const TaskProcessorRegistry = {
  template: TemplateProcessor,
  soap: SOAPProcessor,
};